/**
 * Built-in starting points. Each builds an ordinary definition from the registry, so a template is
 * only offered when the facets and record types it reads exist on this ranch, and the library grows
 * as modules land. Running one is one tap; adjusting it opens the same builder as a custom report.
 */
import type { Registry } from "../../../contract";
import type { ReportDefinition, TemplateDTO, TemplateParam, Topic } from "./types";

export interface ReportTemplate {
  id: string;
  name: string;
  description: string;
  topic: Topic;
  icon: string;
  /** Facets (animal) and record types it needs. `anyEvents`: at least one of these. */
  requires?: { facets?: string[]; events?: string[]; anyEvents?: string[]; finance?: boolean };
  params?: TemplateParam[];
  /** What a worklist from this report is for (shown on the worklist). */
  worklist?: string;
  build(reg: Registry, p: Record<string, unknown>): ReportDefinition;
}

const CALVES = ["calf", "bull_calf", "heifer_calf"];
const days = (p: Record<string, unknown>, key: string, fallback: number) => (typeof p[key] === "number" && (p[key] as number) > 0 ? Math.round(p[key] as number) : fallback);
const present = (reg: Registry, types: string[]) => types.filter((t) => reg.events.has(t));
const hasField = (reg: Registry, type: string, key: string) => !!reg.event(type)?.fields[key];

export const TEMPLATES: ReportTemplate[] = [
  {
    id: "herd_inventory", name: "Herd inventory by place and class", topic: "herd", icon: "cattle",
    description: "How many head are where, by cow, heifer, bull, steer and calf.",
    build: () => ({
      v: 1, subject: "animals", scope: "current", filters: [],
      columns: [{ field: "tag" }, { field: "class" }, { field: "sex" }, { field: "age_years" }, { field: "location" }],
      group_by: ["location", "class"], summary_only: true, sort: [{ field: "tag", dir: "asc" }],
    }),
  },
  {
    id: "cattle_list", name: "Cattle list", topic: "herd", icon: "list",
    description: "Every animal in the herd with class, age, breed, place and groups.",
    build: () => ({
      v: 1, subject: "animals", scope: "current", filters: [],
      columns: [{ field: "tag" }, { field: "name" }, { field: "class" }, { field: "age_years" }, { field: "breed" }, { field: "location" }, { field: "groups" }],
      sort: [{ field: "tag", dir: "asc" }],
    }),
  },
  {
    id: "group_roster", name: "Group roster", topic: "herd", icon: "group",
    description: "The animals in one group, ready to print or check off.",
    params: [{ key: "group", label: "Group", kind: "group", required: true }],
    worklist: "Check each animal in the group.",
    build: (_reg, p) => ({
      v: 1, subject: "animals", scope: "current",
      filters: typeof p.group === "string" && p.group ? [{ field: "groups", op: "in", value: [p.group] }] : [],
      columns: [{ field: "tag" }, { field: "name" }, { field: "class" }, { field: "age_years" }, { field: "breed" }, { field: "location" }],
      sort: [{ field: "tag", dir: "asc" }],
    }),
  },
  {
    id: "missing_birth_date", name: "Animals with no birth date", topic: "records", icon: "calendar",
    description: "Cattle whose age Hondo can't work out yet.",
    worklist: "Find each animal's birth date or best estimate.",
    build: () => ({
      v: 1, subject: "animals", scope: "current", filters: [{ field: "birth_date", op: "is_not_set" }],
      columns: [{ field: "tag" }, { field: "name" }, { field: "class" }, { field: "origin" }, { field: "location" }],
      sort: [{ field: "tag", dir: "asc" }],
    }),
  },
  {
    id: "calves_missing_weaning", name: "Calves without a weaning weight", topic: "performance", icon: "scale",
    description: "Calves old enough to wean that have no weaning weight recorded.",
    requires: { facets: ["performance"] },
    params: [{ key: "min_days", label: "At least this many days old", kind: "days", default: 150 }],
    worklist: "Weigh each calf.",
    build: (_reg, p) => ({
      v: 1, subject: "animals", scope: "current",
      filters: [
        { field: "class", op: "in", value: CALVES },
        { field: "age_days", op: "gte", value: days(p, "min_days", 150) },
        { field: "facet:performance.weights.weaning", op: "is_not_set" },
      ],
      columns: [{ field: "tag" }, { field: "sex" }, { field: "age_months" }, { field: "dam" }, { field: "location" }, { field: "facet:performance.last_weight" }],
      sort: [{ field: "age_days", dir: "desc" }],
    }),
  },
  {
    id: "weaning_by_sire", name: "Average weaning weight by sire", topic: "performance", icon: "scale",
    description: "Each sire's calves with a weaning weight, and their average.",
    requires: { facets: ["performance"] },
    build: () => ({
      v: 1, subject: "animals", scope: "all", filters: [{ field: "facet:performance.weights.weaning", op: "is_set" }],
      columns: [{ field: "tag" }, { field: "sire" }, { field: "sex" }, { field: "birth_date" }, { field: "facet:performance.weights.weaning" }],
      group_by: ["sire"], summary_only: true, totals: [{ field: "facet:performance.weights.weaning", fn: "avg" }],
      sort: [{ field: "tag", dir: "asc" }],
    }),
  },
  {
    id: "open_cows", name: "Open cows", topic: "breeding", icon: "heart",
    description: "Cows and heifers whose latest breeding status is open.",
    requires: { facets: ["breeding"] },
    worklist: "Decide what to do with each open cow.",
    build: () => ({
      v: 1, subject: "animals", scope: "current",
      filters: [{ field: "class", op: "in", value: ["cow", "heifer"] }, { field: "facet:breeding.status", op: "eq", value: "open" }],
      columns: [{ field: "tag" }, { field: "class" }, { field: "age_years" }, { field: "location" },
        { field: "facet:breeding.last_calved" }],
      sort: [{ field: "age_years", dir: "desc" }],
    }),
  },
  {
    id: "due_to_calve", name: "Due to calve", topic: "breeding", icon: "calf",
    description: "Cows with a due date coming up, soonest first.",
    requires: { facets: ["breeding"] },
    params: [{ key: "days", label: "Due in the next days", kind: "days", default: 60 }],
    worklist: "Watch each cow and record the calving.",
    build: (_reg, p) => ({
      v: 1, subject: "animals", scope: "current",
      filters: [{ field: "facet:breeding.due_date", op: "within_next_days", days: days(p, "days", 60) }],
      columns: [{ field: "tag" }, { field: "facet:breeding.due_date" }, { field: "class" }, { field: "location" }, { field: "facet:breeding.calvings" }],
      sort: [{ field: "facet:breeding.due_date", dir: "asc" }],
    }),
  },
  {
    id: "not_preg_checked", name: "Cows not pregnancy checked", topic: "breeding", icon: "list",
    description: "Cows with no pregnancy check recorded this season.",
    requires: { events: ["breeding.preg_check"] },
    params: [{ key: "days", label: "No check in the last days", kind: "days", default: 240 }],
    worklist: "Pregnancy check each cow.",
    build: (_reg, p) => ({
      v: 1, subject: "animals", scope: "current",
      filters: [{ field: "class", op: "eq", value: "cow" }, { field: "events:breeding.preg_check", op: "not_exists", days: days(p, "days", 240) }],
      columns: [{ field: "tag" }, { field: "age_years" }, { field: "location" }, { field: "groups" }, { field: "events:breeding.preg_check", agg: "latest" }],
      sort: [{ field: "tag", dir: "asc" }],
    }),
  },
  {
    id: "calving_book", name: "Calving book", topic: "breeding", icon: "calf",
    description: "Every calving recorded this year, newest first.",
    requires: { events: ["breeding.calving"] },
    params: [{ key: "days", label: "Last days", kind: "days", default: 365 }],
    build: (reg, p) => {
      const def = reg.event("breeding.calving");
      const dam = def?.subjects.find((s) => s.kind === "animal")?.role;
      return {
        v: 1, subject: "events", event_types: ["breeding.calving"], grain: "event", window: { days: days(p, "days", 365) }, filters: [],
        columns: [{ field: "event.date" }, ...(dam ? [{ field: `subject:${dam}` }] : [{ field: "animals" }]), { field: "event.summary" }, { field: "event.recorded_by" }],
        sort: [{ field: "event.date", dir: "desc" }],
      };
    },
  },
  {
    id: "treatments_recent", name: "Treatments in the last 30 days", topic: "health", icon: "syringe",
    description: "Every animal treated or vaccinated recently, one row per animal treated.",
    requires: { anyEvents: ["health.treatment", "health.vaccination", "health.procedure"] },
    params: [{ key: "days", label: "Last days", kind: "days", default: 30 }],
    build: (reg, p) => ({
      v: 1, subject: "events", event_types: present(reg, ["health.treatment", "health.vaccination", "health.procedure"]), grain: "animal_event",
      window: { days: days(p, "days", 30) }, filters: [],
      columns: [{ field: "event.date" }, { field: "animal" }, { field: "event.type" }, { field: "event.summary" }, { field: "event.recorded_by" }],
      sort: [{ field: "event.date", dir: "desc" }],
    }),
  },
  {
    id: "treatment_history", name: "Treatment history", topic: "health", icon: "syringe",
    description: "A year of treatments, one row per animal treated, for audits and buyers.",
    requires: { anyEvents: ["health.treatment", "health.vaccination", "health.procedure"] },
    params: [{ key: "days", label: "Last days", kind: "days", default: 365 }],
    build: (reg, p) => ({
      v: 1, subject: "events", event_types: present(reg, ["health.treatment", "health.vaccination", "health.procedure"]), grain: "animal_event",
      window: { days: days(p, "days", 365) }, filters: [],
      columns: [{ field: "event.date" }, { field: "animal" }, { field: "animal:class" }, { field: "event.type" }, { field: "event.summary" }, { field: "event.recorded_by" }],
      sort: [{ field: "event.date", dir: "desc" }],
    }),
  },
  {
    id: "active_holds", name: "Animals on withdrawal", topic: "health", icon: "warn",
    description: "Cattle with a meat or milk withdrawal hold, so none ship or get milked by mistake.",
    requires: { facets: ["health"] },
    build: () => ({
      v: 1, subject: "animals", scope: "current", filters: [{ field: "facet:health.holds", op: "is_set" }],
      columns: [{ field: "tag" }, { field: "class" }, { field: "location" }, { field: "facet:health.holds" }, { field: "facet:health.last_treatment.on" }],
      sort: [{ field: "tag", dir: "asc" }],
    }),
  },
  {
    id: "death_loss", name: "Death loss", topic: "herd", icon: "warn",
    description: "Deaths in a period, and what share of the herd that was.",
    requires: { events: ["herd.death"] },
    params: [{ key: "days", label: "Last days", kind: "days", default: 365 }],
    build: (reg, p) => ({
      v: 1, subject: "events", event_types: ["herd.death"], grain: "animal_event", window: { days: days(p, "days", 365) }, filters: [], rate: "herd_at_start",
      columns: [{ field: "event.date" }, { field: "animal" }, { field: "animal:class" }, { field: "animal:age_months" },
        ...(hasField(reg, "herd.death", "cause") ? [{ field: "data:cause" }] : []), { field: "event.summary" }],
      sort: [{ field: "event.date", dir: "desc" }],
    }),
  },
  {
    id: "recent_records", name: "Recent records by type", topic: "records", icon: "history",
    description: "Pick the kinds of records and the period; one row per record.",
    params: [{ key: "types", label: "Kinds of records", kind: "event_types" }, { key: "days", label: "Last days", kind: "days", default: 30 }],
    build: (_reg, p) => ({
      v: 1, subject: "events", event_types: Array.isArray(p.types) ? (p.types as string[]) : [], grain: "event", window: { days: days(p, "days", 30) }, filters: [],
      columns: [{ field: "event.date" }, { field: "event.type" }, { field: "event.summary" }, { field: "subjects" }, { field: "event.recorded_by" }],
      sort: [{ field: "event.date", dir: "desc" }],
    }),
  },
  {
    id: "notes_attention", name: "Notes that need attention", topic: "records", icon: "note",
    description: "Notes someone flagged as needing a look.",
    requires: { events: ["core.note"] },
    params: [{ key: "days", label: "Last days", kind: "days", default: 90 }],
    build: (_reg, p) => ({
      v: 1, subject: "events", event_types: ["core.note"], grain: "event", window: { days: days(p, "days", 90) },
      filters: [{ field: "data:attention", op: "is_true" }],
      columns: [{ field: "event.date" }, { field: "event.summary" }, { field: "subjects" }, { field: "event.recorded_by" }],
      sort: [{ field: "event.date", dir: "desc" }],
    }),
  },
  {
    id: "event_log", name: "Record log export", topic: "records", icon: "download",
    description: "Everything recorded in a period, ready to download as a spreadsheet.",
    params: [{ key: "days", label: "Last days", kind: "days", default: 90 }],
    build: (_reg, p) => ({
      v: 1, subject: "events", grain: "event", window: { days: days(p, "days", 90) }, filters: [],
      columns: [{ field: "event.date" }, { field: "event.type" }, { field: "event.summary" }, { field: "subjects" }, { field: "event.recorded_by" }, { field: "event.source" }],
      sort: [{ field: "event.date", dir: "desc" }],
    }),
  },
  {
    id: "place_occupancy", name: "Places and head counts", topic: "land", icon: "place",
    description: "How many head are in each place against what it holds.",
    build: () => ({
      v: 1, subject: "places", filters: [],
      columns: [{ field: "name" }, { field: "kind" }, { field: "head" }, { field: "capacity" }, { field: "use_pct" }, { field: "area" }],
      sort: [{ field: "head", dir: "desc" }], totals: [{ field: "head", fn: "sum" }],
    }),
  },
  {
    id: "stock_on_hand", name: "Stock on hand", topic: "supplies", icon: "stock",
    description: "Every lot with stock left, with when it expires.",
    build: () => ({
      v: 1, subject: "stock", grain: "lot", filters: [{ field: "on_hand", op: "gt", value: 0 }],
      columns: [{ field: "item" }, { field: "code" }, { field: "on_hand" }, { field: "unit" }, { field: "expires_at" }, { field: "days_to_expiry" }, { field: "place" }],
      sort: [{ field: "expires_at", dir: "asc" }],
    }),
  },
  {
    id: "expiring_lots", name: "Expiring soon", topic: "supplies", icon: "clock",
    description: "Medicine, vaccine and feed lots that expire soon and still have stock.",
    params: [{ key: "days", label: "Expires in the next days", kind: "days", default: 90 }],
    build: (_reg, p) => ({
      v: 1, subject: "stock", grain: "lot",
      filters: [{ field: "on_hand", op: "gt", value: 0 }, { field: "expires_at", op: "within_next_days", days: days(p, "days", 90) }],
      columns: [{ field: "item" }, { field: "code" }, { field: "on_hand" }, { field: "unit" }, { field: "expires_at" }, { field: "days_to_expiry" }],
      sort: [{ field: "expires_at", dir: "asc" }],
    }),
  },
  {
    id: "low_stock", name: "Running low", topic: "supplies", icon: "warn",
    description: "Products at or below their reorder point.",
    build: () => ({
      v: 1, subject: "stock", grain: "item", filters: [{ field: "below_reorder", op: "is_true" }],
      columns: [{ field: "name" }, { field: "kind" }, { field: "on_hand" }, { field: "unit" }, { field: "reorder_at" }, { field: "used_30" }],
      sort: [{ field: "name", dir: "asc" }],
    }),
  },
  {
    id: "tasks_overdue", name: "Overdue tasks", topic: "work", icon: "task",
    description: "Open tasks past their due date, most overdue first.",
    build: () => ({
      v: 1, subject: "tasks", filters: [{ field: "status", op: "eq", value: "open" }, { field: "days_overdue", op: "gt", value: 0 }],
      columns: [{ field: "title" }, { field: "due_at" }, { field: "days_overdue" }, { field: "assignee" }, { field: "subject" }],
      sort: [{ field: "days_overdue", dir: "desc" }],
    }),
  },
  {
    id: "spending_by_category", name: "Spending by category", topic: "money", icon: "money",
    description: "Expense and bill lines this year, totaled by category.",
    requires: { finance: true },
    params: [{ key: "days", label: "Last days", kind: "days", default: 365 }],
    build: (_reg, p) => ({
      v: 1, subject: "finance", grain: "line", window: { days: days(p, "days", 365) },
      filters: [{ field: "kind", op: "in", value: ["expense", "bill"] }],
      columns: [{ field: "date" }, { field: "contact" }, { field: "description" }, { field: "category" }, { field: "amount" }],
      group_by: ["category"], summary_only: true, totals: [{ field: "amount", fn: "sum" }], sort: [{ field: "date", dir: "desc" }],
    }),
  },
];

export function templateAvailable(t: ReportTemplate, reg: Registry, canFinance: boolean): boolean {
  const r = t.requires;
  if (!r) return true;
  if (r.finance && !canFinance) return false;
  if (r.facets?.some((id) => !reg.facets.some((f) => f.id === id && f.subject === "animal"))) return false;
  if (r.events?.some((type) => !reg.events.has(type))) return false;
  if (r.anyEvents && !r.anyEvents.some((type) => reg.events.has(type))) return false;
  return true;
}

export function templateDefaults(t: ReportTemplate): Record<string, unknown> {
  return Object.fromEntries((t.params ?? []).filter((p) => p.default !== undefined).map((p) => [p.key, p.default]));
}

export function availableTemplates(reg: Registry, canFinance: boolean): TemplateDTO[] {
  return TEMPLATES.filter((t) => templateAvailable(t, reg, canFinance)).map((t) => ({
    id: t.id, name: t.name, description: t.description, topic: t.topic, icon: t.icon, params: t.params ?? [], worklist: t.worklist,
    definition: t.build(reg, templateDefaults(t)),
  }));
}

export const templateById = (id: string) => TEMPLATES.find((t) => t.id === id);

/** A sensible first report for a subject, before anyone adds filters. */
export function defaultDefinition(subject: ReportDefinition["subject"]): ReportDefinition {
  switch (subject) {
    case "animals": return { v: 1, subject, scope: "current", filters: [], columns: [{ field: "tag" }, { field: "name" }, { field: "class" }, { field: "age_years" }, { field: "location" }], sort: [{ field: "tag", dir: "asc" }] };
    case "events": return { v: 1, subject, grain: "event", window: { days: 30 }, filters: [], columns: [{ field: "event.date" }, { field: "event.type" }, { field: "event.summary" }, { field: "subjects" }, { field: "event.recorded_by" }], sort: [{ field: "event.date", dir: "desc" }] };
    case "groups": return { v: 1, subject, filters: [], columns: [{ field: "name" }, { field: "kind" }, { field: "head" }, { field: "place" }], sort: [{ field: "name", dir: "asc" }] };
    case "places": return { v: 1, subject, filters: [], columns: [{ field: "name" }, { field: "kind" }, { field: "head" }, { field: "capacity" }, { field: "use_pct" }], sort: [{ field: "name", dir: "asc" }] };
    case "stock": return { v: 1, subject, grain: "lot", filters: [{ field: "on_hand", op: "gt", value: 0 }], columns: [{ field: "item" }, { field: "code" }, { field: "on_hand" }, { field: "unit" }, { field: "expires_at" }], sort: [{ field: "expires_at", dir: "asc" }] };
    case "tasks": return { v: 1, subject, filters: [{ field: "status", op: "eq", value: "open" }], columns: [{ field: "title" }, { field: "due_at" }, { field: "assignee" }, { field: "subject" }], sort: [{ field: "due_at", dir: "asc" }] };
    case "finance": return { v: 1, subject, grain: "doc", window: { days: 90 }, filters: [], columns: [{ field: "date" }, { field: "kind" }, { field: "contact" }, { field: "memo" }, { field: "total" }], sort: [{ field: "date", dir: "desc" }] };
  }
}

/** Columns that make sense when a records or stock report switches what one row is. */
export function grainColumns(def: ReportDefinition, grain: ReportDefinition["grain"]): ReportDefinition["columns"] {
  if (def.subject === "events") return def.columns.map((c) => (c.field === "animals" && grain === "animal_event" ? { field: "animal" } : c.field === "animal" && grain === "event" ? { field: "animals" } : c));
  if (def.subject === "stock") return grain === "item" ? [{ field: "name" }, { field: "kind" }, { field: "on_hand" }, { field: "unit" }, { field: "reorder_at" }, { field: "next_expiry" }] : defaultDefinition("stock").columns;
  if (def.subject === "finance") return grain === "line" ? [{ field: "date" }, { field: "contact" }, { field: "description" }, { field: "category" }, { field: "amount" }] : defaultDefinition("finance").columns;
  return def.columns;
}
